import React from 'react'

import { Box, Button, Spinner, Text } from '@factorialco/deprecated-design-system'

import Selector from 'modules/core/components/Selector'
import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useEffect } from 'modules/core/hooks/useEffect'
import { useMemo } from 'modules/core/hooks/useMemo'
import i18n from 'modules/core/lib/i18n'

import { useCustomResourceFields } from 'modules/custom_resources/hooks/customResources/useCustomResourceFields'
import CustomFieldModel from 'modules/custom_resources/store/models/customFields/Field'
import { useFormProvider } from 'modules/processes/components/ItemWizard/Providers/form'
import { usePreviewProvider } from 'modules/processes/components/ItemWizard/Providers/preview'
import { useValidationsProvider } from 'modules/processes/components/ItemWizard/Providers/validations'
import { useTaskWizard } from 'modules/processes/components/ItemWizard/Tasks/TaskProvider'
import { DataCollectionCategories } from 'modules/processes/domain'
import { isStringArray } from 'modules/processes/lib/isStringArray'

interface Iconfiguration {
  field_ids: string[]
}

interface ICategory {
  category: string
  configuration?: Iconfiguration
}

interface IOption {
  label: string
  value: string
}

const CONFIGURATION_FIELD = 'task_type_configuration'

const FieldSelector = () => {
  const { setFieldValidation, removeFieldValidation } = useValidationsProvider()
  const { access: currentUser } = useCurrentSession()
  const { setTaskConfiguration } = useTaskWizard()
  const { setPreviewValues } = usePreviewProvider()
  const [selectedFieldIds, setselectedFieldIds] = React.useState<string[]>([])
  const { associatedTaskItem: item, form } = useFormProvider()
  const { loading: loadingCustomResourceFields, customResourceFields } = useCustomResourceFields({
    authorId: currentUser.id,
    fieldableType: 'LegalEntity',
    usageGroupSlugs: [CustomFieldModel.UsageGroups.contractVersions],
  })

  const currentTaskConfiguration = form.get('task_type_configuration')?.value

  const [selectedCategories, setSelectedCategories] = React.useState<string[]>([])

  const options = useMemo(() => {
    const data: IOption[] = [
      {
        label: i18n.t(
          'processes.process_settings.wizard.tasks.types.contract_data.category_selector.start_dates'
        ),
        value: DataCollectionCategories.start_date,
      },
      {
        label: i18n.t(
          'processes.process_settings.wizard.tasks.types.contract_data.category_selector.role'
        ),
        value: DataCollectionCategories.role,
      },
      {
        label: i18n.t(
          'processes.process_settings.wizard.tasks.types.contract_data.category_selector.salary'
        ),
        value: DataCollectionCategories.salary,
      },
      {
        label: i18n.t(
          'processes.process_settings.wizard.tasks.types.contract_data.category_selector.contract_hours'
        ),
        value: DataCollectionCategories.contract_hours,
      },
    ]

    if (customResourceFields) {
      customResourceFields.forEach((resource) => {
        const { id, labelText } = resource?.field || {}
        data.push({
          label: resource?.legalEntity ? `${labelText} (${resource?.legalEntity?.legalName})` : '',
          value: `${DataCollectionCategories.contract_custom_fields}-${id}`,
        })
      })
    }

    return data
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customResourceFields?.length, loadingCustomResourceFields])

  const extractCategories = (taskTypeConfiguration?: string) => {
    if (!taskTypeConfiguration) return []

    const categories: string[] = (
      JSON.parse(taskTypeConfiguration).configuration_fields?.at(0)?.categories || []
    ).flatMap((category: ICategory) => {
      const { category: categoryValue, configuration } = category
      if (categoryValue === DataCollectionCategories.contract_custom_fields) {
        return configuration?.field_ids.map(
          (id: string) => `${DataCollectionCategories.contract_custom_fields}-${id}`
        )
      }
      return [categoryValue]
    })
    return categories
  }

  useEffect(() => {
    setFieldValidation({
      field: CONFIGURATION_FIELD,
      validation: () => !!selectedCategories.length,
    })

    return () => {
      removeFieldValidation(CONFIGURATION_FIELD)
    }
  }, [selectedCategories.join(',')]) // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const currentCategories = currentTaskConfiguration?.configuration_fields
      ? extractCategories(JSON.stringify(currentTaskConfiguration?.configuration_fields))
      : []
    const hasCategorySelected = currentCategories.length > 0

    if (item && item.taskTypeConfiguration && !hasCategorySelected) {
      const categories = extractCategories(item.taskTypeConfiguration)
      setSelectedCategories(categories)
    } else if (hasCategorySelected && currentCategories) {
      setSelectedCategories(currentCategories)
    } else {
      setSelectedCategories([
        DataCollectionCategories.start_date,
        DataCollectionCategories.role,
        DataCollectionCategories.salary,
        DataCollectionCategories.contract_hours,
      ])
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const onCategoriesChange = (value: unknown) => {
    if (!isStringArray(value)) return

    const data = value

    const feildIds = data
      .filter((category) => category.includes('-'))
      .map((category) => {
        return String(category.split('-')[1])
      })
    setselectedFieldIds(feildIds)
    setSelectedCategories(data)
  }

  useEffect(() => {
    setTaskConfiguration({
      configuration_fields: [
        {
          resource: 'contract',
          categories: selectedCategories.reduce((memo: ICategory[], category) => {
            if (category.includes('-')) {
              if (memo.some((c) => c.category === DataCollectionCategories.contract_custom_fields))
                return memo

              memo.push({
                category: DataCollectionCategories.contract_custom_fields,
                configuration: {
                  field_ids: selectedFieldIds,
                },
              })
              return memo
            }

            memo.push({
              category,
            })

            return memo
          }, []),
        },
      ],
    })

    setPreviewValues({
      selectedFields: selectedFieldIds.map(
        (id) => customResourceFields?.find((resource) => resource?.field?.id === id)?.field
      ),
      selectedCategories,
    })
  }, [selectedCategories.join(','), selectedFieldIds.length]) // eslint-disable-line react-hooks/exhaustive-deps

  const categoriesOptions = options.map((category) => {
    return (
      <Selector.Option
        key={category.value}
        value={category.value}
        label={category.label}
        searchValue={category.label}
      >
        <Box flexDirection="row" gap="s4" width="full" justifyContent="spaceBetween">
          <Text.Normal ellipsis weight="medium">
            {category.label}
          </Text.Normal>
        </Box>
      </Selector.Option>
    )
  })

  if (loadingCustomResourceFields)
    return (
      <Box alignItems="center">
        <Spinner />
      </Box>
    )

  return (
    <Selector.Popover
      label={i18n.t('processes.process_settings.wizard.tasks.types.contract_data.selector_field')}
      placeholder={i18n.t('common.select')}
      blankLabel={i18n.t('common.select')}
      value={selectedCategories}
      onChange={onCategoriesChange}
      mandatory
      isMultiSelect
      hasSearch={false}
      footer={
        selectedCategories.length > 0 ? (
          <Box alignItems="center">
            <Button type="secondary" onClick={() => onCategoriesChange([])}>
              {i18n.t(
                'processes.process_settings.wizard.tasks.types.request_general_information.category_selector.clear_selection'
              )}
            </Button>
          </Box>
        ) : undefined
      }
    >
      {categoriesOptions}
    </Selector.Popover>
  )
}

export default FieldSelector
